import {
    createElement,
    type HTMLAttributes,
    type MouseEvent,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { markdownToHtml } from '@campai/lib-common/utils/misc'
import type { MarkdownOptions } from '@campai/lib-common/utils/misc'

import { prepareMarkdownCodeBlocks } from '@/components/syntaxHighlighting/shiki'

import './Markdown.css'

export type MarkdownProps = {
    rootHtmlElement?: 'span' | 'div'
    html?: string
    className?: string
    inline?: boolean
    syntaxHighlighting?: boolean
    children?: string
    options?: MarkdownOptions
    htmlAttributes?: HTMLAttributes<HTMLElement>
}

export const Markdown = ({
    className,
    inline,
    children,
    options,
    rootHtmlElement,
    syntaxHighlighting,
    html: srcHtml,
    htmlAttributes,
}: MarkdownProps) => {
    const { t } = useTranslation()
    const rootRef = useRef<HTMLElement | null>(null)
    const [expandedCodeBlocks, setExpandedCodeBlocks] = useState<Set<string>>(() => new Set())
    const baseHtml = useMemo(() => {
        if (srcHtml) {
            return srcHtml
        }
        return children ? markdownToHtml(children, inline, options) : ''
    }, [children, inline, options, srcHtml])
    const [html, setHtml] = useState(baseHtml)

    useEffect(() => {
        setExpandedCodeBlocks(new Set())
    }, [baseHtml])

    useEffect(() => {
        let active = true

        if (inline) {
            setHtml(baseHtml)
            return () => {
                active = false
            }
        }

        setHtml(baseHtml)

        void prepareMarkdownCodeBlocks({ html: baseHtml, syntaxHighlighting }).then((highlightedHtml) => {
            if (active) {
                setHtml(highlightedHtml)
            }
        })

        return () => {
            active = false
        }
    }, [baseHtml, inline, syntaxHighlighting])

    useLayoutEffect(() => {
        const root = rootRef.current

        if (!root || inline) {
            return
        }

        root.querySelectorAll<HTMLElement>('.markdown-code-block').forEach((wrapper) => {
            const codeBlockId = wrapper.dataset.markdownCodeBlockId
            const preElement = wrapper.querySelector('pre')
            const expandButton = wrapper.querySelector<HTMLButtonElement>('[data-markdown-expand-code="true"]')

            if (!codeBlockId || !preElement || !expandButton) {
                return
            }

            const expanded = expandedCodeBlocks.has(codeBlockId)
            wrapper.dataset.expanded = expanded ? 'true' : 'false'
            wrapper.dataset.overflow = preElement.scrollHeight > 250 || expanded ? 'true' : 'false'
            expandButton.textContent = t('components.markdown.showMoreCode')
        })
    }, [expandedCodeBlocks, html, inline, t])

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        htmlAttributes?.onClick?.(event)
        if (event.defaultPrevented) {
            return
        }
        const target = event.target as HTMLElement | null
        const expandButton = target?.closest?.('[data-markdown-expand-code="true"]')

        if (expandButton instanceof HTMLButtonElement) {
            const codeBlockWrapper = expandButton.closest('.markdown-code-block')
            const codeBlockId =
                codeBlockWrapper instanceof HTMLElement ? codeBlockWrapper.dataset.markdownCodeBlockId : undefined

            if (codeBlockId) {
                event.preventDefault()
                event.stopPropagation()
                setExpandedCodeBlocks((currentCodeBlockIds) => {
                    const nextCodeBlockIds = new Set(currentCodeBlockIds)
                    nextCodeBlockIds.add(codeBlockId)
                    return nextCodeBlockIds
                })
            }

            return
        }

        const copyButton = target?.closest?.('[data-markdown-copy-code="true"]')

        if (copyButton instanceof HTMLButtonElement) {
            const codeElement = copyButton.closest('.markdown-code-block')?.querySelector('pre code')
            const code = codeElement?.textContent

            if (code) {
                event.preventDefault()
                event.stopPropagation()
                void navigator.clipboard?.writeText(code)
            }

            return
        }

        const image = target instanceof HTMLImageElement ? target : target?.closest?.('img')
        const imageElement = image instanceof HTMLImageElement ? image : null
        const src = imageElement?.getAttribute('src')
        if (!src) {
            return
        }
        event.preventDefault()
        window.open(src, '_blank', 'noopener,noreferrer')
    }

    return createElement(rootHtmlElement || (inline ? 'span' : 'div'), {
        ref: rootRef,
        className: classNames(
            'markdown',
            { 'leading-normal': !inline, preserveEmptyLines: options?.preserveEmptyLines },
            className
        ),
        dangerouslySetInnerHTML: { __html: html },
        ...htmlAttributes,
        onClick: handleClick,
    })
}
